import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { InitializeProjectInput, ProjectCtx } from "../types/project.types";
import { useAppSelector } from "../../../../../redux/hooks/redux";
import { TemplateId } from "../../../templates/types/templates-ids.types";
import { normalizeCompanyNameToSlug } from "../helpers/normalize-company-to-slug.helper";


const ProjectContext = createContext<ProjectCtx | null>(null);

export function ProjectProvider({ children }: { children: React.ReactNode }) {

    const { businessName } = useAppSelector((userState) => userState.userState.company)

    console.log(businessName)

    const businessSlug = normalizeCompanyNameToSlug(businessName)

    const DEFAULTS = {
        activeTemplateId: null as TemplateId,
        projectName: "New Project",
        slugName: businessSlug,
        projectIconDataUrl: null as File | null,
    };


    const [workspace, setWorkspace] = useState(() => DEFAULTS);
    const [isInitialized, setIsInitialized] = useState(false);

    const setActiveTemplateId = useCallback((v: TemplateId) => {
        setWorkspace((prev) => (prev.activeTemplateId === v ? prev : { ...prev, activeTemplateId: v }));
    }, []);

    const setProjectName = useCallback((v: string) => {
        setWorkspace((prev) => (prev.projectName === v ? prev : { ...prev, projectName: v }));
    }, []);

    const setSlugName = useCallback((v: string) => {
        setWorkspace((prev) => (prev.slugName === v ? prev : { ...prev, slugName: v }));
    }, []);

    const handleChangeSlug = useCallback((slug: string) => {
        setSlugName(slug);
    }, [setSlugName]);

    const setProjectIconDataUrl = useCallback((v: File | null) => {
        setWorkspace((prev) => (prev.projectIconDataUrl === v ? prev : { ...prev, projectIconDataUrl: v }));
    }, []);

    const initializeProject = useCallback(
        (data: InitializeProjectInput, opts?: { force?: boolean }) => {
            if (isInitialized && !opts?.force) return;

            setWorkspace((prev) => ({
                activeTemplateId: data.activeTemplateId ?? prev.activeTemplateId,
                projectName: typeof data.projectName === "string" ? data.projectName : prev.projectName,
                slugName: typeof data.slugName === "string" ? data.slugName : prev.slugName,
                projectIconDataUrl: data.projectIconDataUrl,
            }));

            setIsInitialized(true);
        },
        [isInitialized],
    );

    const value = useMemo(
        () => ({
            ...workspace,

            // API
            setActiveTemplateId,
            setProjectName,
            setSlugName,
            handleChangeSlug,
            setProjectIconDataUrl,
            initializeProject,
            isInitialized,
        }),
        [
            workspace,
            setActiveTemplateId,
            setProjectName,
            setSlugName,
            handleChangeSlug,
            initializeProject,
            isInitialized,
        ],
    );

    return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export const useProject = () => {
    const ctx = useContext(ProjectContext);
    if (!ctx) throw new Error("useProject must be used within <ProjectProvider />");
    return ctx;
}